import moment from 'moment';
import { useCallback, useState } from 'react';

import { useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { useLazyGetSocialIntegrationsStatusQuery } from '@app/services/rtkQuery/SocialIntegrations/api';
import type { NSocialIntegrations } from '@app/services/rtkQuery/SocialIntegrations/types';
import { useSocialIntegrationsSnooze } from '@app/shared/components/SocialIntegrationsWarning/hooks/useSocialIntegrationsSnooze';
import type { UseSocialIntegrationsWsOptions } from '@app/shared/components/SocialIntegrationsWarning/hooks/useSocialIntegrationsWs';

const STATUS_STORAGE_KEY = 'socialIntegrationsStatus';

export const useSocialIntegrationsWarning = () => {
  const {
    params: { ActiveBranchId },
  } = useUserPrint();
  const { showNotification } = useNotification();
  const [openModal, setOpenModal] = useState(false);
  const [hasErrors, setHasErrors] = useState(false);
  const [countErrors, setCountErrors] = useState(0);
  const [errorIntegrations, setErrorIntegrations] = useState<
    NSocialIntegrations.ModelSocialIntegrationError[]
  >([]);

  const { isSnoozed, snoozeMenu } = useSocialIntegrationsSnooze();

  const [fetchSocialIntegrationsStatus] =
    useLazyGetSocialIntegrationsStatusQuery();

  const openIntegration = useCallback(async () => {
    try {
      const response = await fetchSocialIntegrationsStatus({
        ActiveBranchId,
      }).unwrap();

      const { CountErrors, HasErrors, ErrorIntegrations, Timestamp } = response;

      setHasErrors(HasErrors);
      setCountErrors(CountErrors);
      setErrorIntegrations(ErrorIntegrations || []);

      localStorage.setItem(
        STATUS_STORAGE_KEY,
        JSON.stringify({
          hasErrors: HasErrors,
          count: CountErrors,
          timestamp: Timestamp ?? moment().toISOString(),
        })
      );

      if (HasErrors) {
        setOpenModal(true);
      }
    } catch (error) {
      showNotification(error);
    }
  }, [fetchSocialIntegrationsStatus, ActiveBranchId, showNotification]);

  const closeModalHandler = useCallback(() => {
    setOpenModal(false);
  }, []);

  const wsChangeHandler = useCallback<
    UseSocialIntegrationsWsOptions['onChange']
  >(({ hasErrors: nextHasErrors, count }) => {
    setHasErrors(nextHasErrors);
    setCountErrors(count);
  }, []);

  const restoreStatusFromCache = useCallback(() => {
    const data = localStorage.getItem(STATUS_STORAGE_KEY);

    if (!data) return;

    try {
      const parsed = JSON.parse(data) as {
        hasErrors?: boolean;
        count?: number;
      };

      setHasErrors(!!parsed.hasErrors);
      setCountErrors(Number(parsed.count) || 0);
    } catch {
      setHasErrors(false);
      setCountErrors(0);
    }
  }, []);

  return {
    openModal,
    hasErrors,
    countErrors,
    errorIntegrations,
    isSnoozed,
    snoozeMenu,
    openIntegration,
    closeModalHandler,
    wsChangeHandler,
    restoreStatusFromCache,
  };
};
